import { SvgIconComponent } from "@mui/icons-material";
import { Stack, TableCell, TableRow, TableSortLabel } from "@mui/material";
import { tableSortLabelClasses } from "@mui/material/TableSortLabel";
import { EllipsisTooltip } from "../../ui/CustomTooltip";
import { Order } from "../../../types";
import { useStateWithSearchParams } from "../../../hooks";
import { ColumnList } from "./columnList";
import { CustomCheckbox } from "../../ui";

const headerTooltip: { [key: string]: string } = {
  Engagement:
    "Average engagement rate of all identified BC members within the account. Engagement rate refers to" +
    " how active and efficient the communication with identified BC members is, based on phone calls, emails," +
    " meetings, and communication recency data.",
  Discovery:
    "Rate of discovered buying committee members within the account, relative to the number of buying" +
    " committee members expected for the account size.",
};

type SearchParams = {
  sort?: string;
  selected?: string;
  page?: string;
  per_page?: string;
};

const CheckboxCell = ({
  selected,
  clearHandler,
}: {
  selected?: string;
  clearHandler: () => void;
}) => {
  if (!selected) return null;
  return (
    <Stack alignItems="center" justifyContent="center">
      <CustomCheckbox indeterminate={true} onClick={clearHandler} />
    </Stack>
  );
};

export const TableHeader = ({
  columns,
  isAccountPlanRunning,
}: {
  columns: ColumnList[];
  isAccountPlanRunning: boolean;
}) => {
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      sort: undefined,
      selected: undefined,
      page: undefined,
      per_page: undefined,
    },
  );

  const handleSort = (columnId: string) => {
    const currentSort = searchDict.sort;
    if (currentSort) {
      const [sort, order] = currentSort.split("-");

      if (sort === columnId) {
        setSearchValue(
          "sort",
          `${sort}-${order === Order.ASC ? Order.DESC : Order.ASC}`,
        );
        return;
      }
    }
    setSearchValue("sort", `${columnId}-${Order.ASC}`);
  };

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isFirefox = /firefox/i.test(navigator.userAgent);

  return (
    <TableRow>
      {columns.map((column, index) => {
        const order =
          (searchDict.sort?.split("-")[1] as "asc" | "desc") || "asc";
        const sort = searchDict.sort?.split("-")[0] || "";

        const isSorted = column.sortable && column.key === sort;
        //@ts-ignore
        const IconComponent: SvgIconComponent | undefined =
          column.sortable && column.iconAsc;

        return (
          <TableCell
            key={`${column.key}-${index}`}
            sx={{
              backgroundColor: index === 0 ? "#E1E6EF" : "transparent",
              borderTopLeftRadius: `${index === 0 ? "20px" : "0"}`,
              width:
                index === 0 && (isSafari || isFirefox)
                  ? "30px"
                  : index === 6 && isAccountPlanRunning
                    ? "200px"
                    : column.maxWidth,

              padding: `${index === 0 ? "2px" : "16px 8px"}`,
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "26px",
              letterSpacing: "0.25px",
              color: "#1F2932",
              textAlign: column.textAlign,
              position: "relative",
            }}
            style={{ ...(column.id === "checkbox" && { padding: 0 }) }}
          >
            {column.sortable ? (
              <TableSortLabel
                active={isSorted}
                direction={isSorted ? order : "desc"}
                onClick={() => handleSort(column.key as string)}
                hideSortIcon={!IconComponent}
                IconComponent={IconComponent || (() => null)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: column.flexAlign,
                  gap: "1px",
                  width: "100%",

                  [`.${tableSortLabelClasses.icon}`]: {
                    opacity: isSorted ? 1 : 0.5,
                    transition: "opacity 0.3s ease",
                    flexShrink: 0,
                    marginLeft: 1,
                  },
                }}
              >
                <EllipsisTooltip tooltip={headerTooltip[column.label]}>
                  {column.label}
                </EllipsisTooltip>
              </TableSortLabel>
            ) : (
              <>
                {column.id === "checkbox" ? (
                  <CheckboxCell
                    selected={searchDict.selected}
                    clearHandler={() => setSearchValue("selected", undefined)}
                  />
                ) : (
                  <EllipsisTooltip tooltip={headerTooltip[column.label]}>
                    {column.label}
                  </EllipsisTooltip>
                )}
              </>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};
